import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded';
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';
import {
    CircularProgress,
    IconButton,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    useTheme,
} from '@mui/material';

import { useDownloadBlobFile } from '@services/queries';

import { regionalKnowledgeFilesTexts } from '../../../constants';
import { useDeleteGeneralKnowledgeFile } from '../../../services/queries';
import { GeneralKnowledgeFile } from '../../../services/types';

interface DocumentActionsProps {
    absolutePath: string;
    name: string;
}

const DocumentActions = ({ absolutePath, name }: DocumentActionsProps) => {
    const theme = useTheme();
    const { mutate: downloadGeneralKnowledgeFile, isPending: isDownloading } =
        useDownloadBlobFile(name);
    const { mutate: deleteGeneralKnowledgeFile, isPending: isDeleting } =
        useDeleteGeneralKnowledgeFile();

    const showPendingIndicator = isDeleting || isDownloading;

    return (
        <Stack direction='row' alignItems='center' justifyContent='flex-end' gap={1}>
            {showPendingIndicator && (
                <CircularProgress size={24} sx={{ color: theme.palette.customColors.purple }} />
            )}
            <IconButton
                onClick={() => downloadGeneralKnowledgeFile(absolutePath)}
                disabled={isDeleting}
            >
                <DownloadRoundedIcon sx={{ color: theme.palette.customColors.purple }} />
            </IconButton>
            <IconButton
                onClick={() => deleteGeneralKnowledgeFile(absolutePath)}
                disabled={isDeleting}
            >
                <DeleteRoundedIcon sx={{ color: theme.palette.customColors.orange }} />
            </IconButton>
        </Stack>
    );
};

interface DocumentListProps {
    documents: GeneralKnowledgeFile[] | null;
}

export const DocumentsList = ({ documents }: DocumentListProps) => {
    const hasDocuments = documents && documents.length > 0;

    if (!hasDocuments)
        return (
            <Typography variant='body2'>{regionalKnowledgeFilesTexts.noDocumentsFound}</Typography>
        );

    return (
        <TableContainer>
            <Table aria-label={regionalKnowledgeFilesTexts.tableMainLabel}>
                <TableHead>
                    <TableRow>
                        <TableCell width='50%'>
                            {regionalKnowledgeFilesTexts.tableNameColumnLabel}
                        </TableCell>
                        <TableCell>
                            {regionalKnowledgeFilesTexts.tableCreatedOnColumnLabel}
                        </TableCell>
                        <TableCell align='right'>
                            {regionalKnowledgeFilesTexts.tableActionsColumnLabel}
                        </TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {documents.map(({ absolutePath, name, createdOn }) => (
                        <TableRow key={name + createdOn}>
                            <TableCell width='50%'>{name}</TableCell>
                            <TableCell>{new Date(createdOn).toLocaleString()}</TableCell>
                            <TableCell align='right'>
                                <DocumentActions absolutePath={absolutePath} name={name} />
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    );
};
